import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import {
  ConfirmDialogContext,
  type ConfirmDialogContextValue,
  type ConfirmOptions,
} from '@/shared/ui/useConfirmDialog'

interface ActiveConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export const ConfirmDialogProvider = ({ children }: PropsWithChildren) => {
  const [active, setActive] = useState<ActiveConfirmState | null>(null)
  const confirmedRef = useRef(false)

  const confirm = useCallback((options: ConfirmOptions) => {
    confirmedRef.current = false
    return new Promise<boolean>((resolve) => {
      setActive({ ...options, resolve })
    })
  }, [])

  const ctxValue = useMemo<ConfirmDialogContextValue>(() => ({ confirm }), [confirm])

  const handleClose = (result: boolean) => {
    if (result) confirmedRef.current = true
    setActive((current) => {
      if (!current) return null
      current.resolve(result)
      return null
    })
  }

  // LEAK-036: evita promise pendente se o provider desmontar com diálogo aberto.
  useEffect(() => {
    return () => {
      setActive((current) => {
        if (!current) return null
        current.resolve(false)
        return null
      })
    }
  }, [])

  return (
    <ConfirmDialogContext.Provider value={ctxValue}>
      {children}
      <ConfirmDialog
        open={Boolean(active)}
        title={active?.title ?? ''}
        description={active?.description}
        confirmLabel={active?.confirmLabel}
        cancelLabel={active?.cancelLabel}
        tone={active?.tone}
        onConfirm={() => handleClose(true)}
        onCancel={() => handleClose(false)}
        confirmedRef={confirmedRef}
      />
    </ConfirmDialogContext.Provider>
  )
}
